import { NextResponse, NextRequest } from 'next/server';
import createMiddleware from 'next-intl/middleware';
import { routing } from './i18n/routing';

// Define base public routes (without locale prefixes)
const BASE_PUBLIC_ROUTES = [
    '/about',
    '/privacy',
    '/beoperator',
    '/contact',
    '/our-works',
    '/details/:id',
    '/details/:id/booking',
    '/delete-account',
];

// Supported locales
const LOCALES = ['en', 'zh', 'ms'];

// Default locale for redirection
const DEFAULT_LOCALE = 'en';

// Generate localized public routes dynamically
const PUBLIC_ROUTES = LOCALES.flatMap((locale) =>
    BASE_PUBLIC_ROUTES.map((route) => `/${locale}${route}`)
);

// Helper function to check if a route matches a public route pattern
const isPublicRoute = (pathname: string): boolean => {
    return PUBLIC_ROUTES.some((route) => {
        // Replace dynamic segments (e.g., :id) with a wildcard regex
        const regex = new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}$`);
        return regex.test(pathname);
    });
};

export default async function middleware(request: NextRequest) {
    const { pathname } = request.nextUrl;

    // Redirect root domain ("/") to the default locale (e.g., "/en")
    if (pathname === '/') {
        return NextResponse.redirect(new URL(`/${DEFAULT_LOCALE}`, request.url));
    }

    // Extract access_token from cookies
    const accessToken = request.cookies.get('access_token')?.value;
    const isAuthenticated = !!accessToken;

    // Check if the route is the landing page (e.g., /en, /zh, /ms)
    if (LOCALES.some((locale) => pathname === `/${locale}`)) {
        // Allow both authenticated and unauthenticated users to access the landing page
        return createMiddleware(routing)(request);
    }

    // Check if the route matches any public route (including dynamic routes)
    if (isPublicRoute(pathname)) {
        // Allow access to public routes
        return createMiddleware(routing)(request);
    }

    // Treat all other routes as protected
    if (!isAuthenticated) {
        // Redirect unauthenticated users to the landing page with the correct locale
        const locale = pathname.split('/')[1]; // Extract the locale (e.g., 'en', 'zh', 'ms')
        console.log('middleware unauthenticated:', locale);

        return NextResponse.redirect(new URL(`/${locale}`, request.url));
    }

    // Allow access to protected routes
    return createMiddleware(routing)(request);
}

export const config = {
    matcher: ['/', '/(zh|en|ms)/:path*'], // Match internationalized pathnames
};